import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import { Link, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as campaignActions from 'app/main/campaigns/store/actions';
import CampaignLoginForm from '../login/CampaignLoginForm';
import RegisterForm from './RegisterForm';
import CampaignEmailLink from '../login/CampaignEmailLink';

const useStyles = makeStyles(theme => ({
	root: {
		background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${darken(
			theme.palette.primary.dark,
			0.5
		)} 100%)`,
		color: theme.palette.primary.contrastText
	},
	leftSection: {},
	rightSection: {
		background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${darken(
			theme.palette.primary.dark,
			0.5
		)} 100%)`,
		color: theme.palette.primary.contrastText
	},
	cardWithIcon: {
		borderRadius: '12px',
		overflow: 'visible',
		position: 'relative'
	},
	headerWithBranding: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '16px 24px',
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default
	},
	heidiLogoSmall: {
		width: 80,
		height: 'auto'
	},
	customIcon: {
		width: 120,
		height: 120,
		objectFit: 'contain',
		marginBottom: theme.spacing(3)
	},
	welcomeMessage: {
		fontSize: '1.5rem',
		fontWeight: 600,
		marginBottom: theme.spacing(2),
		color: theme.palette.text.primary
	},
	campaignName: {
		fontSize: '1.75rem',
		fontWeight: 700,
		marginBottom: theme.spacing(3),
		color: theme.palette.primary.main
	}
}));

const CampaignAuth = props => {
	const classes = useStyles();
	const dispatch = useDispatch();

	// Extract campaign slug from URL params
	const campaignSlug = props.match?.params?.slug;

	// Default to login mode (as specified by user)
	const [authMode, setAuthMode] = useState('login');
	const [loading, setLoading] = useState(true);
	const [campaign, setCampaign] = useState(null);

	// Fetch campaign data on mount
	useEffect(() => {
		if (campaignSlug) {
			setLoading(true);
			dispatch(campaignActions.getCampaignBySlug(campaignSlug))
				.then(data => {
					setCampaign(data);
					setLoading(false);
				})
				.catch(error => {
					console.error('Failed to fetch campaign:', error);
					setLoading(false);
				});
		}
	}, [campaignSlug, dispatch]);

	// If campaign uses passwordless auth, redirect to email link page
	if (campaign && campaign.authenticationMethod === 'passwordless') {
		// Extract email from URL query param if available
		const params = new URLSearchParams(window.location.search);
		const email = params.get('email');
		return <Redirect to={`/join/${campaignSlug}${email ? `?email=${email}` : ''}`} />;
	}

	const handleToggleToSignup = () => {
		setAuthMode('signup');
	};

	const handleToggleToLogin = () => {
		setAuthMode('login');
	};

	return (
		<div
			className={clsx(
				classes.root,
				'flex flex-col flex-auto flex-shrink-0 items-center justify-center p-32'
			)}
		>
			<div className="flex flex-col items-center justify-center w-full">
				<Card className={clsx('w-full max-w-384', campaign?.authPageCustomization?.icon && classes.cardWithIcon)}>
					{campaign?.authPageCustomization?.icon && (
						<div className={classes.headerWithBranding}>
							<img className={classes.heidiLogoSmall} src="assets/images/logos/heidi.svg" alt="Heidi AI" />
						</div>
					)}
					<CardContent className="flex flex-col items-center justify-center p-32">
						{campaign?.authPageCustomization?.icon ? (
							<img 
								className={classes.customIcon} 
								src={campaign.authPageCustomization.icon} 
								alt={campaign.name} 
							/>
						) : (
							<img className="w-128 m-32" src="assets/images/logos/heidi.svg" alt="logo" />
						)}

						{loading ? (
							<CircularProgress />
						) : campaign ? (
							<>
								{campaign.authPageCustomization?.welcomeMessage && (
									<Typography className={classes.welcomeMessage} align="center">
										{campaign.authPageCustomization.welcomeMessage}
									</Typography>
								)}
								<Typography className={classes.campaignName} align="center">
									{campaign.name}
								</Typography>
								<Typography variant="body1" className="text-center md:w-full mb-32" color="textSecondary">
									{authMode === 'login' ? 'Please log in to continue' : 'Create your account to get started'}
								</Typography>

								{authMode === 'login' ? (
									<CampaignLoginForm
										slug={campaignSlug}
										onToggleSignup={handleToggleToSignup}
										campaign={campaign}
									/>
								) : (
									<RegisterForm
										{...props}
										onToggleLogin={handleToggleToLogin}
										campaign={campaign}
									/>
								)}
							</>
						) : (
							<Typography variant="body1" color="error">
								Campaign not found or inactive
							</Typography>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default CampaignAuth;
